import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { getCourseOverviewUrl, getPromptUrl, getAdminUrl, getAdminEditUrl, parseAdminUrl } from './utils/urlUtils';
import { ProgressProvider } from './contexts/ProgressContext';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { RewardProvider } from './contexts/RewardContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Auth from './components/Auth';
import Home from './components/Home';
import Courses from './components/Courses';
import CourseOverview from './components/CourseOverview';
import Profile from './components/Profile';
import Lesson from './components/Lesson';
import Prompts from './components/Prompts';
import PromptDetail from './components/PromptDetail';

import AdminLayout from './components/AdminLayout';
import StudentLayout from './components/StudentLayout';
import AdminAccount from './components/AdminAccount';
import Organizations from './components/Organizations';
import AdminCourses from './components/AdminCourses';
import AdminLessons from './components/AdminLessons';
import AdminPrompts from './components/AdminPrompts';
import CourseEdit from './components/CourseEdit';
import LessonEdit from './components/LessonEdit';
import PromptEdit from './components/PromptEdit';
import ReportingDashboard from './components/ReportingDashboard';
import PasswordReset from './components/PasswordReset';
import AdminSetup from './components/AdminSetup';
import CompleteRegistration from './components/CompleteRegistration';
import AdminRewardItems from './components/AdminRewardItems';
import AdminSuperadmins from './components/AdminSuperadmins';
import AdminStudents from './components/AdminStudents';
import UserStatsDetail from './components/UserStatsDetail';
import RewardDemo from './components/RewardDemo';
import WritersRoom from './components/WritersRoom';
import AdminRoute from './components/AdminRoute';
import ErrorBoundary from './components/ErrorBoundary';
import { appConfig } from './config';
import { isSuperAdmin } from './utils/roleUtils';




function AppContent() {
  const { user, logout, userRole, userOrganizations, loading: authLoading, profileError, refreshUserData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Track location changes (debug - can be removed)
  // React.useEffect(() => {
  //   console.log('🌍 Location changed to:', location.pathname, 'at', new Date().toISOString());
  // }, [location.pathname]);
  
  // State for prompt navigation (still needed)
  const [selectedPromptId, setSelectedPromptId] = useState(null);
  
  // Dynamic page title management
  useEffect(() => {
    const updatePageTitle = () => {
      const path = location.pathname;
      let title = appConfig.name;
      
      if (path === '/courses') {
        title = 'Courses - Storykin';
      } else if (path.startsWith('/courses/') && path.includes('/level/') && path.includes('/lesson/')) {
        // Extract course and lesson info from URL
        const pathParts = path.split('/');
        const courseSlug = pathParts[2];
        const levelNumber = pathParts[4];
        const lessonNumber = pathParts[6];
        const courseName = courseSlug.charAt(0).toUpperCase() + courseSlug.slice(1).replace(/-/g, ' ');
        title = `${courseName} - Level ${levelNumber}, Lesson ${lessonNumber} - Storykin`;
      } else if (path.startsWith('/courses/')) {
        // Course overview page
        const courseSlug = path.split('/')[2];
        const courseName = courseSlug.charAt(0).toUpperCase() + courseSlug.slice(1).replace(/-/g, ' ');
        title = `${courseName} - Storykin`;
      } else if (path === '/prompts') {
        title = 'Prompts - Storykin';
      } else if (path === '/writers-room') {
        title = "Writer's Room - Storykin";
      } else if (path.startsWith('/prompts/')) {
        const promptSlug = path.split('/')[2];
        const promptName = promptSlug.charAt(0).toUpperCase() + promptSlug.slice(1).replace(/-/g, ' ');
        title = `${promptName} - Storykin`;
      } else if (path === '/profile') {
        title = 'Profile - Storykin';
      } else if (path === '/sign-in') {
        title = 'Sign In - Storykin';
      } else if (path === '/sign-up') {
        title = 'Sign Up - Storykin';
      } else if (path === '/home' || path === '/') {
        title = 'Home - Storykin';
      } else if (path === '/complete-registration') {
        title = 'Complete Registration - Storykin';
      } else if (path === '/dashboard') {
        title = 'Dashboard - Storykin';
      } else if (path === '/reset-password') {
        title = 'Reset Password - Storykin';
      } else if (path === '/reward-demo') {
        title = 'Reward Demo - Storykin';
      } else if (path.startsWith('/admin')) {
        // Admin page titles
        const adminParams = parseAdminUrl(path, location.search);
        if (adminParams.isEdit) {
          title = `Edit ${adminParams.section.slice(0, -1)} - Admin - Storykin`;
        } else if (adminParams.section === 'account') {
          title = 'Account Management - Admin - Storykin';
        } else if (adminParams.section === 'organizations') {
          title = 'Organizations - Admin - Storykin';
        } else if (adminParams.section === 'courses') {
          title = 'Course Management - Admin - Storykin';
        } else if (adminParams.section === 'lessons') {
          title = 'Lesson Management - Admin - Storykin';
        } else if (adminParams.section === 'prompts') {
          title = 'Prompt Management - Admin - Storykin';
        } else if (adminParams.section === 'reporting') {
          title = 'Reporting Dashboard - Admin - Storykin';
        } else if (adminParams.section === 'superadmins') {
          title = 'Superadmins - Admin - Storykin';
        } else if (adminParams.section === 'students') {
          title = 'Students - Admin - Storykin';
        } else {
          title = 'Admin - Storykin';
        }
      }
      
      document.title = title;
    };
    
    updatePageTitle();
  }, [location.pathname]);
  
  // Admin mode state
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [adminSection, setAdminSection] = useState('account');
  const [currentAdminOrgId, setCurrentAdminOrgId] = useState(null);
  const [editingOrganizationId, setEditingOrganizationId] = useState(null);
  const [editingCourseId, setEditingCourseId] = useState(null);
  const [editingLessonId, setEditingLessonId] = useState(null);
  const [editingPromptId, setEditingPromptId] = useState(null);
  
  // Track if user has manually switched modes to prevent auto-redirect
  const [hasManuallySetMode, setHasManuallySetMode] = useState(false);

  const handleCourseClick = (course) => {
    navigate(getCourseOverviewUrl(course));
  };

  const handleNavigateToHome = () => {
    navigate('/home');
  };

  const handleBackToCourses = () => {
    setSelectedPromptId(null);
    navigate('/courses');
  };

  const handleNavigateToProfile = () => {
    navigate('/profile');
  };

  const handleLogOut = async () => {
    try {
      await logout();
      setSelectedPromptId(null);
      setIsAdminMode(false);
      setAdminSection('account');
      setCurrentAdminOrgId(null);
      setEditingOrganizationId(null);
      setEditingCourseId(null);
      setEditingLessonId(null);
      setEditingPromptId(null);
      setHasManuallySetMode(false); // Reset manual mode tracking on logout
      navigate('/sign-in');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  /**
   * Handle mode switching between admin and student interfaces
   * Prevents auto-redirect after manual mode selection
   */
  const handleModeSwitch = () => {
    setHasManuallySetMode(true); // Track that user manually switched modes
    if (isAdminMode) {
      // Switch to student mode
      setIsAdminMode(false);
      navigate('/home');
    } else {
      // Switch to admin mode
      setIsAdminMode(true);
      // Ensure regular admins have their organization ID set
      if (userRole === 'admin' && userOrganizations?.length > 0) {
        setCurrentAdminOrgId(userOrganizations[0]);
      }
      navigate(getAdminUrl('account'));
    }
  };

  const handleAdminSectionChange = useCallback((section) => {
    navigate(getAdminUrl(section));
  }, [navigate]);

  // Stable identity so AdminLayout does not treat tab navigation as a new org load.
  const handleAdminOrgChange = useCallback((orgId) => {
    setCurrentAdminOrgId(orgId);
  }, []);

  const handleNavigateToOrganization = (organizationId) => {
    navigate(getAdminEditUrl('organization', organizationId));
  };

  const handleBackToOrganizations = () => {
    navigate(getAdminUrl('organizations'));
  };

  const handleNavigateToCourse = (courseId) => {
    navigate(getAdminEditUrl('course', courseId));
  };

  const handleBackToAdminCourses = () => {
    navigate(getAdminUrl('courses'));
  };

  const handleNavigateToLesson = (lessonId) => {
    navigate(getAdminEditUrl('lesson', lessonId));
  };

  const handleBackToAdminLessons = () => {
    navigate(getAdminUrl('lessons'));
  };

  const handleNavigateToAdminPrompt = (promptId) => {
    navigate(getAdminEditUrl('prompt', promptId));
  };

  const handleBackToAdminPrompts = () => {
    navigate(getAdminUrl('prompts'));
  };

  const handleStartLesson = (courseId, levelId, lessonId) => {
    // This function is no longer needed since we use URL navigation
    // But keeping it for backward compatibility - components should use navigate() directly
    console.warn('handleStartLesson is deprecated - use navigate() with getLessonUrl() instead');
  };

  const handleNavigateToPrompt = (prompt) => {
    setSelectedPromptId(prompt.id);
    navigate(getPromptUrl(prompt));
  };

  const handleBackToPrompts = () => {
    setSelectedPromptId(null);
    navigate('/prompts');
  };

  const handleNavigateToWritersRoom = () => {
    navigate('/writers-room');
  };

  /**
   * Set admin organization ID when user organizations become available
   */
  React.useEffect(() => {
    if (user && userRole === 'admin' && userOrganizations?.length > 0 && !currentAdminOrgId) {
      setCurrentAdminOrgId(userOrganizations[0]);
    }
  }, [user, userRole, userOrganizations, currentAdminOrgId]);

  /**
   * Auto-redirect admins to admin mode on fresh login
   * Only triggers if user hasn't manually chosen a mode and is coming from authentication
   * Does NOT redirect when user is already on a specific lesson page (preserves current page on refresh)
   */
  React.useEffect(() => {
    if (user && userRole && (userRole === 'admin' || userRole === 'superadmin') && !isAdminMode && !hasManuallySetMode) {
      // Check if we're on a student page or root/home, but NOT on a specific lesson or course detail
      const isOnRootPages = location.pathname === '/' || location.pathname === '/home' || 
                           location.pathname === '/courses' || location.pathname === '/prompts';
      const isOnPromptDetailPage = location.pathname.startsWith('/prompt/');
      const isOnLessonPage = location.pathname.includes('/level/') && location.pathname.includes('/lesson/');
      const isOnCourseDetailPage = location.pathname.startsWith('/courses/') && 
                                  !location.pathname.includes('/level/');
      
      // Only redirect if on root pages or prompt pages, but NOT on lesson pages or course detail pages
      // This preserves lesson URLs and course overview URLs on page refresh while still redirecting on fresh login
      if ((isOnRootPages || isOnPromptDetailPage) && !isOnLessonPage && !isOnCourseDetailPage) {
        setIsAdminMode(true);
        // Ensure regular admins have their organization ID set
        if (userRole === 'admin' && userOrganizations?.length > 0) {
          setCurrentAdminOrgId(userOrganizations[0]);
        }
        navigate(getAdminUrl('account'));
      }
    }
  }, [user, userRole, userOrganizations, isAdminMode, location.pathname, hasManuallySetMode, navigate]);

  /**
   * Sync admin state with URL parameters
   */
  React.useEffect(() => {
    if (location.pathname.startsWith('/admin')) {
      const adminParams = parseAdminUrl(location.pathname, location.search);
      
      // Update admin mode and section state
      if (!isAdminMode) {
        setIsAdminMode(true);
      }
      
      // Update admin section
      if (adminParams.isEdit) {
        setAdminSection(`${adminParams.section.slice(0, -1)}-edit`);
        
        // Set editing resource IDs
        if (adminParams.section === 'courses') {
          setEditingCourseId(adminParams.resourceId);
        } else if (adminParams.section === 'lessons') {
          setEditingLessonId(adminParams.resourceId);
        } else if (adminParams.section === 'prompts') {
          setEditingPromptId(adminParams.resourceId);
        }
      } else if (adminParams.section === 'organizations' && adminParams.resourceId) {
        // Organization detail view
        setAdminSection('account');
        setEditingOrganizationId(adminParams.resourceId);
      } else {
        setAdminSection(adminParams.section);
        // Clear editing IDs when not in edit mode
        setEditingCourseId(null);
        setEditingLessonId(null);
        setEditingPromptId(null);
        setEditingOrganizationId(null);
      }
    } else if (isAdminMode && !location.pathname.startsWith('/admin')) {
      // User navigated away from admin, turn off admin mode
      setIsAdminMode(false);
    }
  }, [location.pathname, location.search]);

  // Show loading spinner while auth is loading
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div aria-hidden="true" className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          {/* role="status" ensures screen readers announce the loading message */}
          <div role="status" aria-live="polite" className="mt-4">
            <div className="text-gray-600">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  // Failed profile read (e.g. first login after password reset) must not be
  // treated as a new student — that incorrectly opens Complete Registration.
  if (user && profileError) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div role="alert" className="text-gray-800 text-lg font-medium mb-2">
            Couldn't load your account
          </div>
          <p className="text-gray-600 mb-6">
            This can happen right after a password reset. Please try again.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={() => refreshUserData()}
              className="min-h-[44px] min-w-[44px] px-6 py-2 rounded-full bg-blue-500 text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => logout()}
              className="min-h-[44px] min-w-[44px] px-6 py-2 rounded-full border border-gray-300 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Complete Registration gate: authenticated users without organization assignment
  // (e.g., new Google SSO users) must complete org + ID verification before platform access
  const needsCompleteRegistration =
    user &&
    !profileError &&
    userRole === 'student' &&
    (!userOrganizations || userOrganizations.length === 0);

  if (needsCompleteRegistration) {
    return <CompleteRegistration />;
  }

  // Show auth routes when not logged in
  if (!user) {
    return (
      <Routes>
        <Route path="/sign-in" element={<Auth />} />
        <Route path="/sign-up" element={<Auth />} />
        <Route path="/reset-password" element={<PasswordReset />} />
        <Route path="/admin-setup" element={<AdminSetup />} />
        <Route path="/__/auth/action" element={<AdminSetup />} />
        <Route path="*" element={
          <Navigate 
            to={`/sign-in?returnTo=${encodeURIComponent(location.pathname)}`} 
            replace 
          />
        } />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/home" replace />} />
      
      {/* Redirect authenticated users away from auth pages */}
      <Route path="/sign-in" element={
        <Navigate to={
          new URLSearchParams(location.search).get('returnTo') || '/home'
        } replace />
      } />
      <Route path="/sign-up" element={<Navigate to="/home" replace />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<Navigate to="/admin/account" replace />} />
      
      <Route path="/admin/*" element={
        <AdminRoute>
          <AdminLayout
          activeSection={adminSection}
          onSectionChange={handleAdminSectionChange}
          currentOrgId={currentAdminOrgId}
          onOrgChange={handleAdminOrgChange}
          onModeSwitch={handleModeSwitch}
          onLogOut={handleLogOut}
        >
          <ErrorBoundary context="AdminShell">
          <Routes>
            {/* Admin Account Management */}
            <Route path="account" element={
              <>
                {currentAdminOrgId || editingOrganizationId ? (
                  <AdminAccount 
                    organizationId={editingOrganizationId || currentAdminOrgId} 
                    mode={isSuperAdmin(userRole) ? 'superadmin' : 'admin'}
                    onBackToOrganizations={editingOrganizationId ? handleBackToOrganizations : undefined}
                  />
                ) : (
                  <div className="flex items-center justify-center py-12">
                    <div aria-hidden="true" className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-gray-600">Loading organization...</span>
                  </div>
                )}
              </>
            } />
            
            {/* Organizations — superadmin list; regular admins redirect to account */}
            <Route path="organizations" element={
              isSuperAdmin(userRole) ? (
                <AdminRoute requireSuperAdmin>
                  <Organizations onNavigateToOrganization={handleNavigateToOrganization} />
                </AdminRoute>
              ) : (
                <Navigate to="/admin/account" replace />
              )
            } />
            <Route path="organizations/:orgId" element={
              isSuperAdmin(userRole) ? (
                <AdminAccount
                  mode="superadmin"
                  onBackToOrganizations={handleBackToOrganizations}
                />
              ) : (
                <Navigate to="/admin/account" replace />
              )
            } />
            
            {/* Course Management (SuperAdmin only) */}
            <Route path="courses" element={
              <AdminRoute requireSuperAdmin>
                <AdminCourses onNavigateToCourse={handleNavigateToCourse} />
              </AdminRoute>
            } />
            <Route path="courses/:courseId/edit" element={
              <CourseEdit 
                onBackToCourses={handleBackToAdminCourses}
              />
            } />
            <Route path="courses/new" element={
              <CourseEdit 
                onBackToCourses={handleBackToAdminCourses}
              />
            } />
            
            {/* Lesson Management (SuperAdmin only) */}
            <Route path="lessons" element={
              <AdminRoute requireSuperAdmin>
                <AdminLessons onNavigateToLesson={handleNavigateToLesson} />
              </AdminRoute>
            } />
            <Route path="lessons/:lessonId/edit" element={
              <LessonEdit 
                onBackToLessons={handleBackToAdminLessons}
              />
            } />
            <Route path="lessons/new" element={
              <LessonEdit 
                onBackToLessons={handleBackToAdminLessons}
              />
            } />
            
            {/* Prompt Management (SuperAdmin only) */}
            <Route path="prompts" element={
              <AdminRoute requireSuperAdmin>
                <AdminPrompts onNavigateToPrompt={handleNavigateToAdminPrompt} />
              </AdminRoute>
            } />
            <Route path="prompts/:promptId/edit" element={
              <PromptEdit 
                onBackToPrompts={handleBackToAdminPrompts}
              />
            } />
            <Route path="prompts/new" element={
              <PromptEdit 
                onBackToPrompts={handleBackToAdminPrompts}
              />
            } />
            
            {/* Reporting Dashboard */}
            <Route path="reporting" element={
              <ReportingDashboard />
            } />
            
            {/* Reward Items Setup */}
            <Route path="reward-items" element={
              <AdminRoute requireSuperAdmin>
                <AdminRewardItems />
              </AdminRoute>
            } />
            
            {/* Superadmins Management (SuperAdmin only) */}
            <Route path="superadmins" element={
              <AdminRoute requireSuperAdmin>
                <AdminSuperadmins />
              </AdminRoute>
            } />
            
            {/* Students Management */}
            <Route path="students" element={
              <AdminStudents organizationId={currentAdminOrgId} />
            } />
            <Route path="students/:userId" element={
              <UserStatsDetail />
            } />
          </Routes>
          </ErrorBoundary>
        </AdminLayout>
        </AdminRoute>
      } />
      
      {/* Student Routes */}
      {/* Home Route */}
      <Route path="/home" element={
        <StudentLayout>
          <Home 
            onNavigateToHome={handleNavigateToHome}
            onNavigateToCourses={handleBackToCourses}
            onNavigateToProfile={handleNavigateToProfile}
            onNavigateToPrompts={() => navigate('/prompts')}
            onNavigateToWritersRoom={handleNavigateToWritersRoom}
            onLogOut={handleLogOut}
            onModeSwitch={handleModeSwitch}
            showAdminMode={isAdminMode}
          />
        </StudentLayout>
      } />
      
      <Route path="/courses" element={
        <StudentLayout>
          <Courses 
            onCourseClick={handleCourseClick}
            onNavigateToHome={handleNavigateToHome}
            onNavigateToProfile={handleNavigateToProfile}
            onNavigateToPrompts={() => navigate('/prompts')}
            onNavigateToWritersRoom={handleNavigateToWritersRoom}
            onLogOut={handleLogOut}
            onModeSwitch={handleModeSwitch}
            showAdminMode={isAdminMode}
          />
        </StudentLayout>
      } />
      <Route path="/courses/:courseSlug" element={
        <StudentLayout>
          <CourseOverview 
            onBack={handleBackToCourses}
            onNavigateToHome={handleNavigateToHome}
            onNavigateToProfile={handleNavigateToProfile}
            onNavigateToPrompts={() => navigate('/prompts')}
            onNavigateToWritersRoom={handleNavigateToWritersRoom}
            onLogOut={handleLogOut}
            onStartLesson={handleStartLesson}
            onModeSwitch={handleModeSwitch}
            showAdminMode={isAdminMode}
          />
        </StudentLayout>
      } />
                {/* Lesson route with specific lesson number */}
                <Route path="/courses/:courseSlug/level/:levelNumber/lesson/:lessonNumber" element={
                  <StudentLayout>
                    <Lesson 
                      onNavigateToHome={handleNavigateToHome}
                      onNavigateToProfile={handleNavigateToProfile}
                      onNavigateToPrompts={() => navigate('/prompts')}
                      onNavigateToWritersRoom={handleNavigateToWritersRoom}
                      onLogOut={handleLogOut}
                      onNavigateToCourses={handleBackToCourses}
                      onStartLesson={handleStartLesson}
                      onModeSwitch={handleModeSwitch}
                      showAdminMode={isAdminMode}
                    />
                  </StudentLayout>
                } />
                
                {/* Legacy route - redirect to first lesson in level */}
                <Route path="/courses/:courseSlug/level/:levelNumber" element={
                  <Navigate to="lesson/1" replace />
                } />
      <Route path="/profile" element={
        <StudentLayout>
          <Profile
            onBack={handleNavigateToHome} 
            onNavigateToHome={handleNavigateToHome}
            onNavigateToPrompts={() => navigate('/prompts')}
            onNavigateToWritersRoom={handleNavigateToWritersRoom}
            onLogOut={handleLogOut}
            onNavigateToProfile={handleNavigateToProfile}
            onModeSwitch={handleModeSwitch}
            showAdminMode={isAdminMode}
          />
        </StudentLayout>
      } />
      <Route path="/writers-room" element={
        <StudentLayout>
          <WritersRoom
            onNavigateToHome={handleNavigateToHome}
            onNavigateToCourses={handleBackToCourses}
            onNavigateToProfile={handleNavigateToProfile}
            onNavigateToPrompts={() => navigate('/prompts')}
            onNavigateToWritersRoom={handleNavigateToWritersRoom}
            onLogOut={handleLogOut}
            onModeSwitch={handleModeSwitch}
            showAdminMode={isAdminMode}
          />
        </StudentLayout>
      } />
      <Route path="/prompts" element={
        <StudentLayout>
          <Prompts 
            onNavigateToHome={handleNavigateToHome}
            onNavigateToCourses={handleBackToCourses}
            onNavigateToProfile={handleNavigateToProfile}
            onNavigateToWritersRoom={handleNavigateToWritersRoom}
            onLogOut={handleLogOut}
            onNavigateToPrompt={handleNavigateToPrompt}
            onModeSwitch={handleModeSwitch}
            showAdminMode={isAdminMode}
          />
        </StudentLayout>
      } />
        <Route path="/prompts/:promptSlug" element={
          <StudentLayout>
            <PromptDetail 
              onNavigateToHome={handleNavigateToHome}
              onNavigateToCourses={handleBackToCourses}
              onNavigateToProfile={handleNavigateToProfile}
              onNavigateToWritersRoom={handleNavigateToWritersRoom}
              onLogOut={handleLogOut}
              onBackToPrompts={handleBackToPrompts}
              onModeSwitch={handleModeSwitch}
              showAdminMode={isAdminMode}
            />
          </StudentLayout>
        } />
      {/* Legacy /dashboard route — redirect to the student home page */}
      <Route path="/dashboard" element={<Navigate to="/home" replace />} />
      
      {/* Reward Demo Route (remove in production) */}
      <Route path="/reward-demo" element={
        <div className="min-h-screen bg-gray-50 py-8">
          <RewardDemo />
        </div>
      } />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary context="App">
    <ThemeProvider>
      <AuthProvider>
        <OrganizationProvider>
          <ProgressProvider>
            <RewardProvider>
              <Router>
                <AppContent />
              </Router>
            </RewardProvider>
          </ProgressProvider>
        </OrganizationProvider>
      </AuthProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
